/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// net-name-monitor.js — watches for net-name MERGE conflicts (Feature 120) and
// surfaces them in the notification stack. Two names bind to member addresses;
// a later edit (a new wire) can merge their nets, and then both names point at
// one net. The netlist reports the loser deterministically; this routes it to
// a toast so a name is never silently dropped. Naming stays inert to the
// engine — this only reads the netlist partition, never changes it.
//
// Reads the ONE shared NetlistCache every other consumer uses, rather than
// running its own independent buildNetlist() — that used to mean a held
// pushbutton's transient bridge (tracked only by the shared cache's
// #partStates, via chiphippo:part-state) could merge two named nets without
// this ever noticing, since it neither shared that state nor listened for the
// event at all.

import { t } from "../i18n.js";

export class NetNameMonitor {
  #netlist;
  #notifications;
  #namesOf;

  /**
   * @param {import('./netlist-cache.js').NetlistCache} netlistCache
   * @param {import('./notification-stack.js').NotificationStack} notifications
   * @param {() => number} [nameCount] - how many net names the desk holds.
   *   A merge needs two, and asking the netlist for its conflicts rebuilt it
   *   after every edit (8–17 ms on a big desk) on desks that name nothing.
   */
  constructor(netlistCache, notifications, nameCount = null) {
    this.#netlist = netlistCache;
    this.#notifications = notifications;
    this.#namesOf = nameCount;
    window.addEventListener("chiphippo:doc-changed", () => this.check());
    window.addEventListener("chiphippo:part-state", () => this.check());
  }

  /** Toast every current name-merge conflict from the shared netlist. */
  check() {
    if (this.#namesOf && this.#namesOf() < 2) return;
    const { nameConflicts } = this.#netlist.get();
    for (const c of nameConflicts) {
      this.#notifications.notify({
        // Keyed on the net so a re-settle refreshes rather than stacks.
        key: `netname-conflict:${c.netId}`,
        variant: "warning",
        title: t("netNames.conflict"),
        message: t("netNames.conflictMessage", {
          winner: c.winner,
          loser: c.name,
        }),
      });
    }
  }
}
